import type { ListBuilderConfiguration } from '../types';

/**
 * PD-17066 — a competitor's Products and prices tab: the prices logged
 * against it, under the competitive pricing visibility rules. Rows come from
 * GET /api/competitors/:id/prices.
 *
 * "Our price" is our item list price for now. The plan calls for the
 * customer's own price from our pricing with the last invoiced price beneath
 * it; that needs the per-customer price resolution (contracts, promos) on
 * the server and is not built yet.
 */
const TABLE = 'competitive_price';

export const COMPETITOR_PRICES_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-competitor-prices',
			cacheTTL: 600000,
			name: 'Prices',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'price-item',
					fields: [
						{
							id: 'price-item-description',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item',
								idField: 'itemId',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemDescription', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'price-item-lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: {
								field: 'itemExternalId',
								condition: 'is_not_null',
							},
						},
						{
							id: 'price-item-number',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemExternalId', objectTable: TABLE },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'itemExternalId',
								condition: 'is_not_null',
							},
						},
					],
					headers: [{ id: 'h-item', text: 'Item' }],
					minWidth: 220,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'price-customer',
					fields: [
						{
							id: 'price-client-name',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'client',
								idField: 'clientId',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clientName', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-customer', text: 'Customer' }],
					minWidth: 180,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'price-values',
					fields: [
						{
							id: 'price-their-price',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'price', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'currency',
						},
						{
							id: 'price-values-lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: { field: 'itemPrice', condition: 'is_not_null' },
						},
						{
							id: 'price-our-list-label',
							dataConfig: { type: 'custom_text', customText: 'our list price' },
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
							conditionalVisibility: { field: 'itemPrice', condition: 'is_not_null' },
						},
						{
							id: 'price-our-list-price',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemPrice', objectTable: TABLE },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
							conditionalVisibility: { field: 'itemPrice', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-price', text: 'Their Price' }],
					minWidth: 150,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'price-note',
					fields: [
						{
							id: 'price-note-text',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'note', objectTable: TABLE },
								defaultValue: '-',
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'text',
							truncate: true,
							maxLength: 120,
						},
					],
					headers: [{ id: 'h-note', text: 'Note' }],
					minWidth: 180,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'price-logged',
					fields: [
						{
							id: 'price-created-date',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'createdDate', objectTable: TABLE },
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'date',
						},
						{
							id: 'price-logged-lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
							conditionalVisibility: { field: 'creatorName', condition: 'is_not_null' },
						},
						{
							id: 'price-creator-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'creatorName', objectTable: TABLE },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
							conditionalVisibility: { field: 'creatorName', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-logged', text: 'Logged' }],
					minWidth: 130,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Competitive Price',
			objectTable: TABLE,
			// Rows are competitive_price records keyed by `id`.
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'price', fieldLabel: 'Their Price' },
				{ fieldName: 'vendorName', fieldLabel: 'Competitor As Typed' },
				{ fieldName: 'note', fieldLabel: 'Note' },
				{ fieldName: '_created_date', fieldLabel: 'Created' },
			],
		},
		{
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [{ fieldName: 'itemId', fieldLabel: 'ID' }],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Customer',
			objectTable: 'client',
			selectedFields: [{ fieldName: 'clientId', fieldLabel: 'ID' }],
		},
	],
};
